import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Error as ErrorIcon,
  Check
} from '@mui/icons-material';
import PlanCard from './PlanCard';
import ProjectCreationPrompt from '../ProjectCreationPrompt';
import axios from 'axios';
import API_BASE from '../../apiBase';
import { getStoredUser } from '../../utils/userStorage';
import { ensureUserBootstrapped } from '../../utils/ensureUserBootstrapped';
import { createCheckoutSession } from '../../utils/razorpay';
import { trackButtonClick, trackFormSubmit } from '../../utils/gtm';

const PlansSection = ({ hideHeader = false }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [llmType, setLlmType] = useState('byo'); // 'byo' or 'qdex'
  const [userTrialStatus, setUserTrialStatus] = useState(null);
  const [showProjectPrompt, setShowProjectPrompt] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const [downgradePlan, setDowngradePlan] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showSuccessRedirectDialog, setShowSuccessRedirectDialog] = useState(false);
  const [redirectSeconds, setRedirectSeconds] = useState(10);

  useEffect(() => {
    const initializePlans = async () => {
      await ensureUserBootstrapped();
      fetchPlans();
      checkUserTrialStatus();
      getCurrentSubscription();
    };
    initializePlans();
  }, []);

  const footerFeatures = [
    { icon: <Check fontSize='0.75rem' />, label: 'Enterprise Security' },
    { icon: <Check fontSize='0.75rem' />, label: 'Fast Analysis' },
    { icon: <Check fontSize='0.75rem' />, label: '24/7 Support' },
    { icon: <Check fontSize='0.75rem' />, label: 'Regular Updates' }
  ];

  // Check for payment status and handle accordingly
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');

    if (paymentStatus === 'cancelled') {
      // Show error message for cancelled payment
      setPaymentError('Payment was cancelled. Please try again.');
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'failed') {
      // Show error message for failed payment
      setPaymentError('Payment failed. Please try again or contact support.');
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'verification_failed') {
      // Show error message for payment verification failure
      setPaymentError('Payment verification failed. Please try again or contact support if the issue persists.');
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'success') {
      // Show success modal and delay verification/redirect to allow webhook processing
      setShowSuccessRedirectDialog(true);
      setRedirectSeconds(10);

      const countdown = setInterval(() => {
        setRedirectSeconds((s) => (s > 0 ? s - 1 : 0));
      }, 1000);

      const timer = setTimeout(() => {
        verifyPaymentAndRedirect();
        setShowSuccessRedirectDialog(false);
      }, 10000);

      return () => {
        clearInterval(countdown);
        clearTimeout(timer);
      };
    }
  }, []);

  // Check if user has trial subscription
  const checkUserTrialStatus = async () => {
    try {
      const userInfo = getStoredUser() || {};
      const userEmail = userInfo.email;

      if (!userEmail) {
        setUserTrialStatus('no_user');
        return;
      }

      // Authorization handled by axios interceptor (Firebase ID token)

      // Check trial eligibility using the new API endpoint
      const eligibilityResponse = await axios.get(`${API_BASE}/api/payments/check-trial-eligibility`);

      if (eligibilityResponse.data.is_eligible) {
        setUserTrialStatus('no_trial');
      } else {
        setUserTrialStatus('trial_consumed');
      }
    } catch (error) {
      console.error('Error checking trial status:', error);
      setUserTrialStatus('error');
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/subscription-plans`);

      if (response.data) {
        // Transform the data to match our component structure
        const transformedPlans = response.data.map((plan) => {
          return {
            id: plan._id || plan.id,
            name: plan.name,
            price: plan.price,
            currency: plan.currency || 'INR',
            billingCycle: plan.billing_cycle || 'monthly',
            maxPRs: plan.max_prs_per_month || 0,
            maxProjects: plan.max_projects || 0,
            maxTeamMembers: plan.max_team_members === 0 ? 'Unlimited' : (plan.max_team_members || 0),
            maxRepositories: plan.max_repositories === 0 ? 'Unlimited' : (plan.max_repositories || 0),
            aiProvidedByUs: !!(plan.ai_provided_by_us || plan.aiProvidedByUs),
            description: plan.description || getDefaultDescription(plan.name, !!(plan.ai_provided_by_us || plan.aiProvidedByUs)),
            features: Array.isArray(plan.features) ? plan.features : [],
          };
        });
        const PLAN_ORDER = { 'Trial': 0, 'Pro': 1, 'Enterprise': 2, 'Enterprise+': 3 };
        transformedPlans.sort((a, b) => (PLAN_ORDER[a.name] ?? 99) - (PLAN_ORDER[b.name] ?? 99));
        setPlans(transformedPlans);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setLoading(false);
    }
  };

  const getDefaultDescription = (planName, aiProvidedByUs) => {
    const llmText = aiProvidedByUs ? 'and QDEX LLM' : '';

    switch (planName) {
      case 'Trial':
        return `Perfect for trying our AI-powered code review platform ${llmText}`;
      case 'Pro':
        return `Perfect for small teams and individual developers ${llmText}`;
      case 'Enterprise':
        return `Ideal for growing organizations and development teams ${llmText}`;
      case 'Enterprise+':
        return `For enterprise organizations with unlimited needs ${llmText}`;
      default:
        return `AI-powered code review platform ${llmText}`;
    }
  };

  // Returns the default price for a plan, adjusting for llmType === 'qdex'
  const getDefaultPrice = (plan, price) => {
    // No hardcoded overlay; pricing comes from backend plan entity
    return price;
  };

  const formatCurrency = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'USD').toUpperCase() }).format(amount);
    } catch (e) {
      return `${amount.toFixed ? amount.toFixed(2) : amount} ${currency || ''}`;
    }
  };

  const getCurrentSubscription = async () => {
    try {

      // Get current active subscription for display purposes
      const currentResponse = await axios.get(`${API_BASE}/api/payments/current-subscription`);

      // Get latest subscription (active or expired) for downgrade logic
      const latestResponse = await axios.get(`${API_BASE}/api/payments/latest-subscription`);

      // For display purposes, use current subscription
      setCurrentSubscription(currentResponse.data.subscription);
      setCurrentPlan(currentResponse.data.plan);

      // For downgrade logic, use latest subscription (even if expired)
      if (latestResponse.data.has_subscription && latestResponse.data.subscription && latestResponse.data.subscription.end_date) {
        window.latestSubscriptionForDowngrade = latestResponse.data;
      } else {
        window.latestSubscriptionForDowngrade = undefined;
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const verifyPaymentAndRedirect = async () => {
    try {
      // Get payment ID from URL
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('payment_id');

      if (!paymentId) {
        setPaymentError('Payment verification failed. Please try again or contact support if the issue persists.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Get specific payment by ID
      const response = await axios.get(`${API_BASE}/api/payments/${paymentId}`);
      const payment = response.data;

      if (!payment) {
        setPaymentError('Payment not found. Please try again or contact support if the issue persists.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      if (payment.status === 'success') {
        // Check if this was a downgrade payment
        const urlParams = new URLSearchParams(window.location.search);
        const isDowngrade = urlParams.get('downgrade');
        const maxProjects = urlParams.get('max_projects');

        if (isDowngrade === 'true' && maxProjects) {
          // Redirect to project selection page for downgrade
          window.location.href = `/app/project-selection?payment_id=${paymentId}&max_projects=${maxProjects}`;
        } else {
          // Payment verified, redirect to projects page with success parameter
          window.location.href = '/app/projects?payment=success&payment_id=' + paymentId;
        }
      } else if (payment.status === 'pending') {
        // Found a pending payment - might be a webhook issue
        setPaymentError(
          'Payment processing is taking longer than expected. ' +
          'If your card was charged, please contact support with your payment details. ' +
          'Do not attempt to pay again to avoid double charges. ' +
          'Payment ID: ' + paymentId
        );
      } else {
        // Payment failed or other status
        setPaymentError('Payment verification failed. Please try again or contact support if the issue persists.');
      }
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('Error verifying payment:', error);
      setPaymentError('Payment verification failed. Please try again or contact support if the issue persists.');
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleSelectPlan = async (planId, planName) => {
    // Handle plan selection - integrate with payment system
    trackButtonClick(`select_plan_${planName.toLowerCase().replace(/\s+/g, '_')}`, 'plans_section_manage_subscription', {
      plan_name: planName,
      plan_id: planId,
      llm_type: llmType
    });

    // If it's a trial plan and user has already consumed trial, show message
    if (planName === 'Trial' && userTrialStatus === 'trial_consumed') {
      alert('You have already used your trial subscription. Please choose a paid plan.');
      return;
    }

    // If it's a trial plan and user hasn't consumed trial, create trial payment first
    if (planName === 'Trial' && userTrialStatus === 'no_trial') {
      await handleTrialCreation(planId);
      return;
    }

    // If it's a trial plan but userTrialStatus is not set properly, still allow trial
    if (planName === 'Trial' && (userTrialStatus === null || userTrialStatus === 'error' || userTrialStatus === 'no_user')) {
      await handleTrialCreation(planId);
      return;
    }

    // Check if this is a downgrade
    console.log('DEBUG: handleSelectPlan - currentPlan:', currentPlan);
    console.log('DEBUG: handleSelectPlan - planName:', planName);
    console.log('DEBUG: handleSelectPlan - currentSubscription:', currentSubscription);
    console.log('DEBUG: handleSelectPlan - latestSubscriptionForDowngrade:', window.latestSubscriptionForDowngrade);

    // For downgrade logic, use the latest subscription (even if expired)
    const downgradeData = window.latestSubscriptionForDowngrade;
    const downgradePlan = downgradeData?.plan;
    const downgradeSubscription = downgradeData?.subscription;

    if (downgradePlan && planName !== 'Trial') {
      const planHierarchy = {
        'Trial': 0,
        'Pro': 1,
        'Enterprise': 2,
        'Enterprise+': 3
      };

      const currentPlanLevel = planHierarchy[downgradePlan.name] || 0;
      const selectedPlanLevel = planHierarchy[planName] || 0;

      console.log('DEBUG: handleSelectPlan - downgradePlan:', downgradePlan.name);
      console.log('DEBUG: handleSelectPlan - currentPlanLevel:', currentPlanLevel);
      console.log('DEBUG: handleSelectPlan - selectedPlanLevel:', selectedPlanLevel);
      console.log('DEBUG: handleSelectPlan - isDowngrade:', selectedPlanLevel < currentPlanLevel);

      // If selected plan is lower tier than current plan, check if downgrade is allowed
      if (selectedPlanLevel < currentPlanLevel) {
        console.log('DEBUG: handleSelectPlan - downgrade detected, checking subscription expiry');

        // Check if current subscription has expired
        if (downgradeSubscription) {
          const endDate = new Date(downgradeSubscription.end_date);
          const now = new Date();

          console.log('DEBUG: handleSelectPlan - subscription endDate:', endDate);
          console.log('DEBUG: handleSelectPlan - now:', now);
          console.log('DEBUG: handleSelectPlan - isExpired:', endDate <= now);

          // For expired subscriptions, allow downgrade
          if (endDate && endDate > now) {
            // Subscription hasn't expired yet, show message with the correct expiry source
            const expiryText = new Date(downgradeSubscription.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            alert(`Downgrade is not available until your current subscription expires on ${expiryText}.`);
            return;
          }
        }

        console.log('DEBUG: handleSelectPlan - showing downgrade dialog');
        // Downgrade is allowed, show downgrade warning
        const selectedPlanData = plans.find(p => p.id === planId);
        setDowngradePlan({ id: planId, name: planName, maxProjects: selectedPlanData?.maxProjects || 0 });
        setShowDowngradeDialog(true);
        return;
      } else {
        console.log('DEBUG: handleSelectPlan - not a downgrade (selected plan level >= current plan level)');
      }
    } else {
      console.log('DEBUG: handleSelectPlan - downgrade conditions not met: downgradePlan exists:', !!downgradePlan, 'planName is Trial:', planName === 'Trial');
    }

    // For paid plans, show payment dialog
    if (planName !== 'Trial') {
      setSelectedPlan({ id: planId, name: planName });
      setShowPaymentDialog(true);
      return;
    }
  };

  const handleTrialCreation = async (planId) => {
    trackFormSubmit('trial_creation', 'plans_section_manage_subscription', false, {
      plan_id: planId
    });
    try {
      const ready = await ensureUserBootstrapped();
      if (!ready) {
        alert('Your session is still loading. Please wait a moment and try again.');
        return;
      }

      // Create trial payment via API
      const response = await axios.post(`${API_BASE}/api/payments/create-trial`);

      if (response.data.success) {
        trackFormSubmit('trial_creation', 'plans_section_manage_subscription', true, {
          plan_id: planId
        });
        // Navigate to projects page - trial payment exists so user will have access
        window.location.href = '/app/projects';
      } else {
        trackFormSubmit('trial_creation', 'plans_section_manage_subscription', false, {
          plan_id: planId,
          error: 'Failed to start trial'
        });
        alert('Failed to start trial. Please try again.');
      }
    } catch (error) {
      console.error('Error creating trial:', error);
      trackFormSubmit('trial_creation', 'plans_section_manage_subscription', false, {
        plan_id: planId,
        error: error?.message
      });
      alert('Failed to start trial. Please try again.');
    }
  };

  const handlePayment = async () => {
    if (!selectedPlan) return;

    trackButtonClick('proceed_to_payment', 'plans_section_manage_subscription', {
      plan_name: selectedPlan.name,
      plan_id: selectedPlan.id
    });
    setPaymentLoading(true);
    setPaymentError(null);

    try {
      // Check if this is a downgrade
      const isDowngrade = currentPlan && selectedPlan.name !== 'Trial';
      const planHierarchy = {
        'Trial': 0,
        'Pro': 1,
        'Enterprise': 2,
        'Enterprise+': 3
      };

      const currentPlanLevel = planHierarchy[currentPlan?.name] || 0;
      const selectedPlanLevel = planHierarchy[selectedPlan.name] || 0;
      const isDowngradePayment = isDowngrade && selectedPlanLevel < currentPlanLevel;

      // Create checkout session
      const session = await createCheckoutSession(selectedPlan.id, 'monthly');

      // Add downgrade parameters to URL if applicable
      let checkoutUrl = session.checkout_url;
      if (isDowngradePayment) {
        const selectedPlanData = plans.find(p => p.id === selectedPlan.id);
        const maxProjects = selectedPlanData?.maxProjects || 0;
        const separator = checkoutUrl.includes('?') ? '&' : '?';
        checkoutUrl += `${separator}downgrade=true&max_projects=${maxProjects}`;
      }

      // Show our own summary then invoke Razorpay Checkout.js with redirect
      setPendingSession(session);
      setShowPaymentDialog(false);
      setShowSummaryDialog(true);

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.message || 'Failed to process payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Launch Razorpay Checkout with subscription_id and redirect URLs
  const launchCheckout = (session) => {
    const options = {
      key: session.key_id,
      subscription_id: session.subscription_id,
      name: 'Qdex Consulting',
      description: `${selectedPlan.name} - ${'monthly'}`,
      redirect: false,
      handler: function () {
        window.location.href = `/plans?payment=success&payment_id=${session.session_id}`;
      },
      modal: { escape: false, confirm_close: true },
      notes: { payment_id: session.session_id, plan_id: selectedPlan.id },
      prefill: {},
      theme: { color: '#0A65FF' }
    };
    // Ensure script exists
    if (!window.Razorpay) {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => { new window.Razorpay(options).open(); };
      document.body.appendChild(s);
    } else {
      new window.Razorpay(options).open();
    }
  };

  const getNextDueDateString = (cycle) => {
    const now = new Date();
    let next;
    if (cycle === 'yearly') {
      next = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
    } else {
      next = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }
    return next.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleProjectCreated = async (projectData) => {
    try {

      const isUpdating = !!projectData.id;
      const url = isUpdating ? `${API_BASE}/api/projects/${projectData.id}` : `${API_BASE}/api/projects`;
      const method = isUpdating ? 'put' : 'post';

      // Transform data for the backend
      const payload = {
        name: projectData.name,
        description: projectData.description || '',
        business_context: projectData.business_context || '',
        group_id: projectData.group_id,
        repository_type: projectData.repository_type,
        analyzer_type: projectData.analyzer_type,
        analyzer_config: { api_key: projectData.analyzer_api_key },
        jira_enabled: projectData.jira_enabled || false,
        azure_boards_enabled: projectData.azure_boards_enabled || false,
        trello_enabled: projectData.trello_enabled || false,
        zoho_sprints_enabled: projectData.zoho_sprints_enabled || false,
        created_by_info: projectData.created_by_info,
        repository_config: {
          user: projectData.repository_type === 'bitbucket' ? projectData.bitbucket_user :
            projectData.repository_type === 'azure_devops' ? projectData.azure_organization : undefined,
          token: projectData.repository_type === 'github' ? projectData.github_token :
            projectData.repository_type === 'bitbucket' ? projectData.bitbucket_token :
              projectData.repository_type === 'azure_devops' ? projectData.azure_token : undefined,
          project: projectData.repository_type === 'azure_devops' ? projectData.azure_project : undefined,
        },
        jira_config: projectData.jira_enabled ? {
          api_token: projectData.jira_token,
          base_url: projectData.jira_url,
          username: projectData.jira_user,
        } : undefined,
        azure_boards_config: projectData.azure_boards_config,
        trello_config: projectData.trello_config,
        zoho_sprints_config: projectData.zoho_sprints_config,
        user_emails: projectData.user_emails,
        review_comment_mode: projectData.review_comment_mode || 'inline',
        compliance: projectData.compliance,
        control_preference: projectData.control_preference,
      };

      await axios[method](url, payload);
      console.log('Project created successfully!');

      // Close the dialog and navigate to dashboard
      setShowProjectPrompt(false);

      // Navigate to dashboard after successful project creation
      window.location.href = '/dashboard';
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to create project.';
      console.error('Project creation error:', errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handleCancelSubscription = async () => {
    trackButtonClick('cancel_subscription_confirm', 'plans_section_manage_subscription', {
      plan_name: currentPlan?.name
    });
    try {
      setCancelLoading(true);
      await axios.post(`${API_BASE}/api/payments/cancel-subscription`);

      // Refresh subscription data
      await getCurrentSubscription();

      setShowCancelDialog(false);
      // Show success message
      alert('Subscription cancelled successfully. Your subscription will end at the current billing period.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const getSubscriptionExpiryDate = () => {
    if (!currentSubscription) return null;

    // Parse the end_date from the subscription
    const endDate = new Date(currentSubscription.end_date);
    return endDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isButtonDisabled = (plan) => {
    // Disable if it's the current active plan (exact match)
    if (currentPlan && currentPlan.id === plan.id) {
      return true;
    }

    // Disable trial plan if user has consumed trial or has an active subscription
    if (plan.name === 'Trial') {
      return userTrialStatus === 'trial_consumed' || currentSubscription !== null;
    }

    // Disable same-tier or lower-tier plans while subscription is active
    if (currentPlan && currentSubscription) {
      const planHierarchy = {
        'Trial': 0,
        'Pro': 1,
        'Enterprise': 2,
        'Enterprise+': 3
      };

      const currentPlanLevel = planHierarchy[currentPlan.name] || 0;
      const selectedPlanLevel = planHierarchy[plan.name] || 0;

      if (selectedPlanLevel <= currentPlanLevel) {
        return true;
      }
    }

    return false;
  };

  const isCurrentPlan = (plan) => {

    return currentPlan && currentPlan.id === plan.id;
  };

  if (loading || subscriptionLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
          Loading plans...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: '1rem',
              color: '#1f1f1f',
              fontSize: '1.875rem',
              lineHeight: '2.25rem'
            }}
          >
            Choose the Perfect Plan for Your Team
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#737373',
              fontSize: '1.125rem',
              maxWidth: '42rem',
              mx: 'auto',
              lineHeight: '1.75rem'
            }}
          >
            Unlock the full potential of AI-powered code reviews. Start your free trial today or upgrade for advanced features.
          </Typography>
        </Box>

        {/* LLM Toggle */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 6
        }}>
          <ToggleButtonGroup
            value={llmType}
            exclusive
            onChange={(event, newValue) => {
              if (newValue !== null) {
                trackButtonClick(`llm_toggle_${newValue}`, 'plans_section_manage_subscription', {
                  previous_llm_type: llmType,
                  new_llm_type: newValue
                });
                setLlmType(newValue);
              }
            }}
            sx={{
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              p: 0.5,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: 1,
                px: 6,
                py: 1.5,
                textTransform: 'uppercase',
                fontWeight: 500,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                color: '#737373',
                '&.Mui-selected': {
                  bgcolor: 'white',
                  color: '#1f1f1f',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'white',
                  }
                },
                '&:hover': {
                  bgcolor: 'transparent',
                  color: '#1f1f1f',
                }
              }
            }}
          >
            <ToggleButton value="byo">
              BYO LLM
            </ToggleButton>
            <ToggleButton value="qdex">
              QDEX LLM
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Plans Grid */}
      <Grid container spacing={3}>
        {(() => {
          // Filter plans by provider based on toggle
          const filteredPlans = plans.filter(p => (llmType === 'qdex' ? p.aiProvidedByUs : !p.aiProvidedByUs));
          return filteredPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={3} key={plan.id}>
              <PlanCard
                plan={{
                  ...plan,
                  price: getDefaultPrice(plan, plan.price),
                  description: getDefaultDescription(plan.name)
                }}
                isCurrentPlan={isCurrentPlan(plan)}
                onSelectPlan={handleSelectPlan}
                isButtonDisabled={isButtonDisabled(plan)}
                llmType={llmType}
                onCancelSubscription={() => {
                  trackButtonClick('cancel_subscription', 'plans_section_manage_subscription', {
                    plan_name: plan.name
                  });
                  setShowCancelDialog(true);
                }}
              />
            </Grid>
          ));
        })()}
      </Grid>

      {/* Footer */}
      <Box sx={{
        mt: 6,
        textAlign: 'center',
      }}>
        <Typography variant="h6" sx={{
          color: '#374151',
          fontWeight: 600,
          mb: 3
        }}>
          All plans include
        </Typography>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}>
          {footerFeatures.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: '#f9fafb',
                px: 2,
                py: 1,
                borderRadius: 3,
                border: '1px solid #e5e7eb'
              }}
            >
              {feature.icon}
              <Typography variant="body2" sx={{
                color: '#374151',
                fontWeight: 500,
                fontSize: '0.675rem'
              }}>
                {feature.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Project Creation Prompt */}
      <ProjectCreationPrompt
        open={showProjectPrompt}
        onClose={() => {
          setShowProjectPrompt(false);
        }}
        onSave={handleProjectCreated}
        project={null}
      />

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCardIcon color="primary" />
            <Typography variant="h6">Complete Your Subscription</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box>
              <Typography variant="body1" gutterBottom>
                You're about to subscribe to the <strong>{selectedPlan.name}</strong> plan.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                You'll be redirected to our secure payment processor to complete your subscription.
              </Typography>
              {paymentError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {paymentError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              trackButtonClick('payment_dialog_cancel', 'plans_section_manage_subscription');
              setShowPaymentDialog(false);
            }} 
            disabled={paymentLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            disabled={paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={20} /> : <CreditCardIcon />}
          >
            {paymentLoading ? 'Processing...' : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Redirect Dialog */}
      <Dialog open={showSuccessRedirectDialog} onClose={() => setShowSuccessRedirectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Check color="success" />
            <Typography variant="h6">Payment Successful</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
            <CircularProgress color="success" thickness={5} size={36} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center' }}>
              Finalizing your subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              You will be redirected to your projects page in <strong>{redirectSeconds}s</strong>.
            </Typography>
            <Box sx={{ width: '100%', mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, ((10 - redirectSeconds) / 10) * 100))}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
              This helps ensure your payment is confirmed and access is enabled.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              trackButtonClick('go_to_projects_now', 'plans_section_manage_subscription');
              setShowSuccessRedirectDialog(false);
              verifyPaymentAndRedirect();
            }}
            variant="contained"
            disabled={redirectSeconds > 5}
          >
            Go to Projects Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary Dialog */}
      <Dialog open={showSummaryDialog} onClose={() => setShowSummaryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCardIcon color="primary" />
            <Typography variant="h6">Review Subscription</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPlan && pendingSession && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f9fafb' }}>
                <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 0.5 }}>Plan</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedPlan.name}</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>{getDefaultDescription(selectedPlan.name)}</Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 0.5 }}>Amount</Typography>
                  <Typography variant="h6">
                    {(() => {
                const plan = plans.find(p => p.id === selectedPlan.id);
                const amount = getDefaultPrice(plan, plan?.price || 0);
                return formatCurrency(amount, plan?.currency || 'USD');
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 0.5 }}>Billing Cycle</Typography>
                  <Typography variant="h6">Monthly</Typography>
                </Box>
                <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 0.5 }}>Next Due On</Typography>
                  <Typography variant="h6">{getNextDueDateString('monthly')}</Typography>
                </Box>
                <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 0.5 }}>Session</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{pendingSession.session_id}</Typography>
                </Box>
              </Box>

              {(() => {
                const plan = plans.find(p => p.id === selectedPlan.id);
                if (!plan?.features || plan.features.length === 0) return null;
                return (
                  <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1 }}>Included features</Typography>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                      {plan.features.map((f, idx) => (
                        <li key={idx}>
                          <Typography variant="body2">{f}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                );
              })()}

              <Typography variant="body2" color="text.secondary">
                You'll complete your subscription on Razorpay. You will be redirected back here automatically after payment.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              trackButtonClick('summary_dialog_back', 'plans_section_manage_subscription');
              setShowSummaryDialog(false);
            }} 
            disabled={paymentLoading}
          >
            Back
          </Button>
          <Button
            onClick={() => {
              trackButtonClick('summary_dialog_proceed_payment', 'plans_section_manage_subscription', {
                plan_name: selectedPlan?.name,
                plan_id: selectedPlan?.id
              });
              if (pendingSession) launchCheckout(pendingSession);
              setShowSummaryDialog(false);
            }}
            variant="contained"
            disabled={paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={20} /> : <CreditCardIcon />}
          >
            Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon color="error" />
            <Typography variant="h6">Cancel Subscription</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to cancel your subscription?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Your subscription will remain active until the end of your current billing period ({getSubscriptionExpiryDate()}).
              After that, you won't be charged again and your access will be limited.
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> This action cannot be undone. You can resubscribe at any time.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              trackButtonClick('cancel_dialog_keep_subscription', 'plans_section_manage_subscription');
              setShowCancelDialog(false);
            }} 
            disabled={cancelLoading}
          >
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="contained"
            color="error"
            disabled={cancelLoading}
            startIcon={cancelLoading ? <CircularProgress size={20} /> : <ErrorIcon />}
          >
            {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Confirmation Dialog */}
      <Dialog open={showDowngradeDialog} onClose={() => setShowDowngradeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCardIcon color="warning" />
            <Typography variant="h6">Downgrade Confirmation</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {downgradePlan && (
            <Box>
              <Typography variant="body1" gutterBottom>
                You're about to downgrade to the <strong>{downgradePlan.name}</strong> plan.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                <strong>Important:</strong> This plan allows a maximum of <strong>{downgradePlan.maxProjects}</strong> projects.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                After successful payment, you'll be redirected to select which projects to keep active (up to {downgradePlan.maxProjects} projects).
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Projects not selected will be deactivated but can be reactivated if you upgrade again.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              trackButtonClick('downgrade_dialog_cancel', 'plans_section_manage_subscription');
              setShowDowngradeDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              trackButtonClick('proceed_with_downgrade', 'plans_section_manage_subscription', {
                downgrade_plan_name: downgradePlan?.name,
                downgrade_plan_id: downgradePlan?.id
              });
              setShowDowngradeDialog(false);
              setSelectedPlan({ id: downgradePlan.id, name: downgradePlan.name });
              setShowPaymentDialog(true);
            }}
            variant="contained"
            color="warning"
          >
            Proceed with Downgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Error Modal */}
      <Dialog open={!!paymentError} onClose={() => setPaymentError(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCardIcon color="error" />
            <Typography variant="h6">Payment Error</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {paymentError}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              trackButtonClick('payment_error_dialog_ok', 'plans_section_manage_subscription');
              setPaymentError(null);
            }} 
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlansSection;
